import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Text, Flex } from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { analyticsService, AmenityMentionEntry } from '../../services/analyticsService';

interface TopAmenitiesChartProps {
  businessIdentifier?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

const TopAmenitiesChart: React.FC<TopAmenitiesChartProps> = ({
  businessIdentifier,
}) => {
  const [data, setData] = useState<AmenityMentionEntry[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!businessIdentifier) {
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await analyticsService.getAmenityMentions(businessIdentifier);
        setData(result.data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch amenity mentions data';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [businessIdentifier]);

  const getChartOption = () => {
    if (!data || data.length === 0) return {};

    // Sort by count desc, top 15
    const sorted = [...data].sort((a, b) => b.count - a.count).slice(0, 15);
    // Reverse for horizontal bar (highest at top)
    const reversed = [...sorted].reverse();
    const amenities = reversed.map((d) => d.amenity);
    const counts = reversed.map((d) => d.count);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        textStyle: { fontFamily: FONT_FAMILY },
        formatter: (params: any) => {
          const idx = params[0]?.dataIndex;
          const item = reversed[idx];
          if (!item) return '';
          return `<b>${item.amenity}</b><br/>Mentions: ${item.count}`;
        },
      },
      grid: {
        left: '3%',
        right: '15%',
        bottom: '3%',
        top: '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#999',
        },
        splitLine: { lineStyle: { color: '#EDF2F7' } },
      },
      yAxis: {
        type: 'category',
        data: amenities,
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#666',
          width: 130,
          overflow: 'truncate',
        },
        axisLine: { lineStyle: { color: '#E2E8F0' } },
      },
      series: [
        {
          type: 'bar',
          data: counts.map((count) => ({
            value: count,
            itemStyle: {
              color: '#4A90D9',
              borderRadius: [0, 4, 4, 0],
            },
          })),
          barWidth: '50%',
          label: {
            show: true,
            position: 'right',
            fontFamily: FONT_FAMILY,
            fontSize: 11,
            formatter: '{c}',
          },
        },
      ],
    };
  };

  const hasData = data && data.length > 0;

  return (
    <ChartCard title="Top Amenities Mentioned" isLoading={isLoading} error={error}>
      {hasData ? (
        <ReactECharts
          option={getChartOption()}
          style={{ height: Math.max(300, Math.min(data!.length, 15) * 32) + 'px', width: '100%' }}
          opts={{ renderer: 'svg' }}
        />
      ) : (
        <Flex align="center" justify="center" minH="200px">
          <Text color="gray.400" fontSize="sm" fontFamily={FONT_FAMILY}>
            Coming Soon — Data will appear as new conversations are processed
          </Text>
        </Flex>
      )}
    </ChartCard>
  );
};

export default TopAmenitiesChart;
